// Announcement API Types

export type AnnouncementCategory = 0 | 1; // 0: general, 1: popup
export type AnnouncementCategoryString = 'general' | 'popup'; // For UI display

// Utility functions for category conversion
export const categoryToInt = (category: AnnouncementCategoryString): AnnouncementCategory => {
  return category === 'general' ? 0 : 1;
};

export const categoryToString = (category: AnnouncementCategory): AnnouncementCategoryString => {
  return category === 0 ? 'general' : 'popup';
};

export const categoryToChinese = (category: AnnouncementCategory): string => {
  return category === 0 ? '一般公告' : '彈窗公告';
};

// Supplier selection object for form UI and API requests
export interface ISupplierOption {
  label: string;
  value: number;
  key: string; // String for DebounceSelect compatibility
}

// Removed unused types: AnnouncementTimeType, TargetAudience, AnnouncementStatus, InteractionType
// Logic: announcement_time -> derived from release_date_from presence
// target_audience -> derived from target_suppliers (null = all, ISupplierOption[] = specific)
// status -> derived from release_date_from/to vs current time
// interaction_type -> simplified to single confirmation action

export interface ICreateAnnouncementPayload {
  title: string; // max 75 chars
  content: string; // Contains Markdown-formatted links (link field removed)
  category: AnnouncementCategory;
  requires_user_confirmation: boolean;
  requires_main_account_confirmation: boolean;
  target_suppliers: ISupplierOption[] | null; // null means all suppliers
  is_sticky: boolean; // Required in backend
  release_date_from: string; // Required in backend
  release_date_to: string; // Required in backend
}

// PUT /announcement/{aid} - Limited update fields only
export interface IUpdateAnnouncementPayload {
  title: string;
  content: string; // Contains Markdown-formatted links (link field removed)
  is_sticky: boolean | null;
  release_date_to: string | null;
}

export interface IAnnouncement {
  id: number;
  title: string;
  content: string; // Contains Markdown-formatted links (link field removed)
  category: AnnouncementCategory;
  requires_user_confirmation: boolean;
  requires_main_account_confirmation: boolean;
  target_suppliers: ISupplierOption[] | null; // null means all suppliers
  created_at: string;
  updated_at: string;
  is_sticky: boolean; // Backend always returns boolean
  release_date_from: string; // Backend always returns string
  release_date_to: string; // Backend always returns string
}

export interface IGetAnnouncementArgs {
  page?: string;
  limit?: string;
  q?: string;
  category?: AnnouncementCategory;
  release_date_from?: string;
  release_date_to?: string;
  is_sticky?: boolean;
}

// Legacy interface - will be removed as interactions are simplified to confirmations only
// export interface IAnnouncementInteraction { ... }

// Interface for announcement consent records (GET /announcement/confirm/log)
export interface IAnnouncementConsentRecord {
  id: number;
  supplier_id: number;
  email: string;
  is_main_account: boolean;
  confirmed_at: string;
  announcement: {
    id: number;
    title: string;
    category: AnnouncementCategory;
    start_at: string;
  };
  supplier: {
    name: string;
  };
}

// Legacy interfaces - removed with DSS_B API simplification
// export interface IRecordInteractionPayload { ... }
// export interface IUserConfirmationStatus { ... }  
// export interface IPopupAnnouncementGroup { ... }

// ===== DSS_B (Supplier-side) API Interfaces =====

// GET /announcements query parameters (DSS_B) - Supplier announcement pagination
export interface ISupplierAnnouncementQuery {
  page?: string;
  limit?: string;
  q?: string; // Search query for title/content
}

// GET /announcements response (DSS_B) - Simplified supplier announcement list
export interface ISupplierAnnouncement {
  id: number;
  title: string;
  content: string; // Contains Markdown-formatted links (link field removed)
  is_sticky: boolean;
  release_date_from: string;
}

// GET /announcements/check - Main account status check
export interface IAccountCheckResponse {
  is_main_account: boolean;
  is_main_account_consented: boolean;
  supplier_id: number;
  supplier_user_id: string;
}

// GET /announcements/popups - Popup notification lists
export interface IPopupListResponse {
  popup_consent: IPopupAnnouncement[];  // Announcements requiring user confirmation
  popup_info: IPopupAnnouncement[];     // Non-confirmation announcements (can be skipped)
}

// Popup announcement item (subset of full announcement)
export interface IPopupAnnouncement {
  id: number;
  title: string;
  content: string; // Contains Markdown-formatted links (link field removed)
}

// Legacy interface - replaced by IAccountCheckResponse and IPopupListResponse
// ⚠️ DEPRECATED: Use IAccountCheckResponse and IPopupListResponse instead
export interface IAnnouncementConfirmResponse {
  is_user_confirmed: boolean;
  is_main_account_confirmed: boolean;
  master_account_email: string;
  popup: IPopupAnnouncement[];
}